import { createGlobalStyle, css } from "styled-components";

const scrollbar = css`
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(155, 135, 245, 0.3);
    border-radius: 9999px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(155, 135, 245, 0.5);
  }

  body.landing-dark ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
  }

  body.landing-dark ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const GlobalStyle = createGlobalStyle`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html, body, #root {
    height: 100%;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.fontFamily.body}, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
    color: ${({ theme }) => theme.colors.text.body};
    background: linear-gradient(to bottom, white, #f9fafb);
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.fontFamily.display}, system-ui, sans-serif;
  }

  body.landing-dark {
    color: ${({ theme }) => theme.colors.landing.text.primary};
    background: ${({ theme }) => theme.colors.landing.background.gradient};
  }

  a {
    transition: all 0.2s ease;
  }

  a:hover {
    transform: translateY(-1px);
  }

  a:active {
    transform: translateY(0);
  }

  /* Utility Classes */
  .gradient-text {
    background: ${({ theme }) => theme.colors.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .gradient-bg {
    background: ${({ theme }) => theme.colors.gradients.primary};
  }

  .gradient-hero-bg {
    background: ${({ theme }) => theme.colors.gradients.hero};
  }

  .glass {
    background: ${({ theme }) => theme.colors.glass.light.background};
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid ${({ theme }) => theme.colors.glass.light.border};
  }

  body.dark .glass {
    background: ${({ theme }) => theme.colors.glass.dark.background};
    border-color: ${({ theme }) => theme.colors.glass.dark.border};
  }

  .shadow-soft {
    box-shadow: ${({ theme }) => theme.shadows.soft};
  }

  .shadow-glow {
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }

  .card-hover {
    transition: all 0.5s ease-out;
  }

  .card-hover:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  /* Legacy glass card classes */
  .glass-card {
    border-radius: ${({ theme }) => theme.radii.card}px;
    background: ${({ theme }) => theme.colors.landing?.glass?.background || theme.colors.glass.bg};
    backdrop-filter: blur(20px);
    border: 1px solid
      ${({ theme }) => theme.colors.landing?.glass?.border || theme.colors.glass.border};
    box-shadow: ${({ theme }) => theme.shadows.glass};
  }

  .glass-card-hover:hover {
    background: ${({ theme }) => theme.colors.landing?.glass?.hover || "rgba(255, 255, 255, 0.2)"};
  }

  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }

  @keyframes pulse-slow {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.02); }
  }

  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes fade-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scale-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Animation Utility Classes */
  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out;
  }

  .animate-slideIn {
    animation: slideIn 0.6s ease-out;
  }

  .animate-float {
    animation: float 6s ease-in-out infinite;
  }

  .animate-pulse-slow {
    animation: pulse-slow 4s ease-in-out infinite;
  }

  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient-shift 8s ease infinite;
  }

  .animate-fade-up {
    animation: fade-up 0.6s ease-out forwards;
  }

  .animate-fade-up-delay-1 {
    animation: fade-up 0.6s ease-out 0.1s forwards;
    opacity: 0;
  }

  .animate-fade-up-delay-2 {
    animation: fade-up 0.6s ease-out 0.2s forwards;
    opacity: 0;
  }

  .animate-fade-up-delay-3 {
    animation: fade-up 0.6s ease-out 0.3s forwards;
    opacity: 0;
  }

  .animate-scale-in {
    animation: scale-in 0.5s ease-out forwards;
  }

  /* Reduced Motion Support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  ${scrollbar}
`;

